import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Checkbox } from "@/components/ui/checkbox";
import { Badge } from "@/components/ui/badge";
import { supabase } from "@/integrations/supabase/client";
import { useAccountGroups, AccountGroup } from "@/hooks/use-account-groups";
import {
  Loader2,
  Plus,
  Pencil,
  Trash2,
  FolderOpen,
  Folder,
  Building,
  Briefcase,
  Wallet,
  CreditCard,
  Home,
  Store,
  Factory,
} from "lucide-react";
import * as LucideIcons from "lucide-react";

const groupColors = [
  "#10b981", "#3b82f6", "#8b5cf6", "#f59e0b",
  "#ef4444", "#ec4899", "#14b8a6", "#f97316",
];

const groupIcons = [
  "Folder", "Building", "Briefcase", "Wallet",
  "CreditCard", "Home", "Store", "Factory",
];

interface AvailableAccount {
  id: string;
  name: string;
  type: string;
  color: string;
}

interface GroupFormData {
  name: string;
  description: string;
  color: string;
  icon: string;
  accountIds: string[];
}

const defaultFormData: GroupFormData = {
  name: "",
  description: "",
  color: groupColors[0],
  icon: "Folder",
  accountIds: [],
};

function renderGroupIcon(iconName: string, className: string = "h-4 w-4") {
  const IconComponent = (LucideIcons as any)[iconName];
  if (!IconComponent) return <Folder className={className} />;
  return <IconComponent className={className} />;
}

export function AccountGroupsSection() {
  const { groups, loading, createGroup, updateGroup, deleteGroup } = useAccountGroups();
  const [accounts, setAccounts] = useState<AvailableAccount[]>([]);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingGroup, setEditingGroup] = useState<AccountGroup | null>(null);
  const [deleteGroupId, setDeleteGroupId] = useState<string | null>(null);
  const [formData, setFormData] = useState<GroupFormData>(defaultFormData);
  const [isSaving, setIsSaving] = useState(false);

  // Fetch available accounts
  useEffect(() => {
    const fetchAccounts = async () => {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data } = await supabase
        .from("accounts")
        .select("id, name, type, color")
        .order("name");

      setAccounts((data || []) as AvailableAccount[]);
    };
    fetchAccounts();
  }, []);

  const openCreateDialog = () => {
    setEditingGroup(null);
    setFormData(defaultFormData);
    setDialogOpen(true);
  };

  const openEditDialog = (group: AccountGroup) => {
    setEditingGroup(group);
    setFormData({
      name: group.name,
      description: group.description || "",
      color: group.color,
      icon: group.icon,
      accountIds: group.accounts.map(a => a.id),
    });
    setDialogOpen(true);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) return;
    setIsSaving(true);

    if (editingGroup) {
      await updateGroup(editingGroup.id, {
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        color: formData.color,
        icon: formData.icon,
        accountIds: formData.accountIds,
      });
    } else {
      await createGroup({
        name: formData.name.trim(),
        description: formData.description.trim() || undefined,
        color: formData.color,
        icon: formData.icon,
        accountIds: formData.accountIds,
      });
    }

    setIsSaving(false);
    setDialogOpen(false);
  };

  const handleDelete = async () => {
    if (!deleteGroupId) return;
    await deleteGroup(deleteGroupId);
    setDeleteGroupId(null);
  };

  const toggleAccount = (accountId: string) => {
    setFormData(prev => ({
      ...prev,
      accountIds: prev.accountIds.includes(accountId)
        ? prev.accountIds.filter(id => id !== accountId)
        : [...prev.accountIds, accountId],
    }));
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="flex items-center gap-2">
                <FolderOpen className="h-5 w-5" />
                Grupos de Contas
              </CardTitle>
              <CardDescription>
                Agrupe suas contas para relatórios consolidados (DRE)
              </CardDescription>
            </div>
            <Button size="sm" onClick={openCreateDialog}>
              <Plus className="h-4 w-4 mr-2" />
              Novo Grupo
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {groups.length === 0 ? (
            <div className="text-center py-6 text-muted-foreground">
              <FolderOpen className="h-12 w-12 mx-auto mb-3 opacity-50" />
              <p>Nenhum grupo criado ainda.</p>
              <p className="text-sm">Crie um grupo para organizar suas contas.</p>
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map(group => (
                <div
                  key={group.id}
                  className="flex items-center justify-between p-3 rounded-lg border"
                >
                  <div className="flex items-center gap-3">
                    <div
                      className="flex items-center justify-center h-9 w-9 rounded-md"
                      style={{ backgroundColor: group.color + "20", color: group.color }}
                    >
                      {renderGroupIcon(group.icon, "h-5 w-5")}
                    </div>
                    <div>
                      <p className="text-sm font-medium">{group.name}</p>
                      <p className="text-xs text-muted-foreground">
                        {group.accounts.length === 0
                          ? "Nenhuma conta"
                          : group.accounts.length === 1
                          ? "1 conta"
                          : `${group.accounts.length} contas`}
                      </p>
                    </div>
                    {group.description && (
                      <Badge variant="secondary" className="text-xs">
                        {group.description}
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8"
                      onClick={() => openEditDialog(group)}
                    >
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="h-8 w-8 text-destructive hover:text-destructive"
                      onClick={() => setDeleteGroupId(group.id)}
                    >
                      <Trash2 className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>
              {editingGroup ? "Editar Grupo" : "Novo Grupo"}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {/* Name */}
            <div className="space-y-2">
              <Label htmlFor="group-name">Nome</Label>
              <Input
                id="group-name"
                value={formData.name}
                onChange={e => setFormData(prev => ({ ...prev, name: e.target.value }))}
                placeholder="Ex: Pessoal, Empresa..."
              />
            </div>

            {/* Description */}
            <div className="space-y-2">
              <Label htmlFor="group-description">
                Descricao <span className="text-muted-foreground">(opcional)</span>
              </Label>
              <Input
                id="group-description"
                value={formData.description}
                onChange={e => setFormData(prev => ({ ...prev, description: e.target.value }))}
                placeholder="Breve descricao do grupo"
              />
            </div>

            {/* Color */}
            <div className="space-y-2">
              <Label>Cor</Label>
              <div className="flex gap-2 flex-wrap">
                {groupColors.map(color => (
                  <button
                    key={color}
                    type="button"
                    className={`h-8 w-8 rounded-full border-2 transition-all ${
                      formData.color === color
                        ? "border-foreground scale-110"
                        : "border-transparent hover:scale-105"
                    }`}
                    style={{ backgroundColor: color }}
                    onClick={() => setFormData(prev => ({ ...prev, color }))}
                  />
                ))}
              </div>
            </div>

            {/* Icon */}
            <div className="space-y-2">
              <Label>Icone</Label>
              <div className="flex gap-2 flex-wrap">
                {groupIcons.map(iconName => (
                  <button
                    key={iconName}
                    type="button"
                    className={`flex items-center justify-center h-9 w-9 rounded-md border-2 transition-all ${
                      formData.icon === iconName
                        ? "border-foreground bg-muted"
                        : "border-transparent hover:bg-muted/50"
                    }`}
                    onClick={() => setFormData(prev => ({ ...prev, icon: iconName }))}
                  >
                    {renderGroupIcon(iconName, "h-5 w-5")}
                  </button>
                ))}
              </div>
            </div>

            {/* Accounts multi-select */}
            <div className="space-y-2">
              <Label>Contas</Label>
              {accounts.length === 0 ? (
                <p className="text-sm text-muted-foreground">Nenhuma conta disponivel.</p>
              ) : (
                <div className="max-h-48 overflow-y-auto space-y-2 border rounded-md p-3">
                  {accounts.map(account => (
                    <div key={account.id} className="flex items-center gap-3">
                      <Checkbox
                        id={`account-${account.id}`}
                        checked={formData.accountIds.includes(account.id)}
                        onCheckedChange={() => toggleAccount(account.id)}
                      />
                      <label
                        htmlFor={`account-${account.id}`}
                        className="flex items-center gap-2 text-sm cursor-pointer flex-1"
                      >
                        <div
                          className="h-3 w-3 rounded-full"
                          style={{ backgroundColor: account.color }}
                        />
                        {account.name}
                        <span className="text-xs text-muted-foreground">
                          {account.type === "checking"
                            ? "Corrente"
                            : account.type === "savings"
                            ? "Poupanca"
                            : "Cartao"}
                        </span>
                      </label>
                    </div>
                  ))}
                </div>
              )}
            </div>

            {/* Actions */}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button onClick={handleSave} disabled={isSaving || !formData.name.trim()}>
                {isSaving && <Loader2 className="h-4 w-4 animate-spin mr-2" />}
                {editingGroup ? "Salvar" : "Criar"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <AlertDialog open={!!deleteGroupId} onOpenChange={(open) => { if (!open) setDeleteGroupId(null); }}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Excluir grupo?</AlertDialogTitle>
            <AlertDialogDescription>
              Esta acao nao pode ser desfeita. O grupo sera removido, mas as contas
              nao serao afetadas.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
              onClick={handleDelete}
            >
              Excluir
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
